// PRC-032/035: cabecera única de las pantallas de Pricing (docs/pricing/32_SISTEMA_VISUAL.md).
// Compacta (~96 px sin filtros): línea 1 «Volver a …» + sección en mayúsculas;
// línea 2 título de 24 px con un dato de contexto al lado y las acciones a la
// derecha; filtros en barra propia fija debajo. Sin textos de presentación largos.
import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, ButtonBase } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useBackTarget } from '../navigation';
import { J } from '../journeyTokens';

export interface PageHeaderBack { label: string; onClick: () => void }

export interface PageHeaderProps {
  // Etiqueta de sección en mayúsculas (nombre del menú).
  eyebrow: string;
  title: React.ReactNode;
  // Dato de contexto junto al título («Últimos 12 meses · 48.210 líneas»).
  // PRC-035: un texto largo de presentación no se muestra (más de 90 caracteres).
  description?: React.ReactNode;
  // «Volver»: explícito, o 'origin' para mostrarlo solo si se llegó con origen.
  back?: PageHeaderBack | 'origin' | null;
  // Datos clave junto al título (etiquetas pequeñas: identificador, país, estado…).
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  // Filtros globales (periodo, comparación): barra fina que se queda fija.
  filters?: React.ReactNode;
  sx?: object;
}

export const HeaderTag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 22, px: '8px', borderRadius: '6px', border: `1px solid ${J.border}`, bgcolor: 'rgba(148,163,184,.06)', color: J.muted, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
    {children}
  </Box>
);

export const BackLink: React.FC<PageHeaderBack> = ({ label, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{ gap: '4px', color: J.skyText, fontSize: 13, fontWeight: 700, fontFamily: J.font, borderRadius: '6px', px: '2px', '&:hover': { color: '#bae6fd' }, '&.Mui-focusVisible': { outline: `2px solid ${J.sky}`, outlineOffset: 2 } }}
  >
    <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
    {label}
  </ButtonBase>
);

const PageHeader: React.FC<PageHeaderProps> = ({ eyebrow, title, description, back, meta, actions, filters, sx }) => {
  const origin = useBackTarget('');
  const context = typeof description === 'string' && description.length > 90 ? null : description;
  const backLink = back === 'origin'
    ? (origin.fromOrigin ? { label: origin.label, onClick: origin.go } : null)
    : back ?? null;
  return (
    <>
      <Box component="header" sx={{ minWidth: 0, mb: filters ? 1.5 : 2.5, fontFamily: J.font, ...sx }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: 22, flexWrap: 'wrap' }}>
          {backLink && <BackLink {...backLink} />}
          {backLink && <Box component="span" aria-hidden sx={{ width: '1px', height: 12, bgcolor: J.borderStrong }} />}
          <Box component="span" sx={{ color: J.skyText, fontSize: 11.5, fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase' }}>{eyebrow}</Box>
        </Box>
        <Box sx={{ mt: '4px', display: 'flex', alignItems: { xs: 'flex-start', md: 'center' }, justifyContent: 'space-between', gap: { xs: 1.5, md: 3 }, flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <Box component="h1" sx={{ m: 0, color: J.text, fontSize: { xs: 22, md: 24 }, lineHeight: 1.2, fontWeight: 800, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>{title}</Box>
            {context && (
              <Box component="span" title={typeof context === 'string' ? context : undefined} sx={{ color: J.muted, fontSize: 13.5, maxWidth: { xs: '100%', md: 520 }, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {context}
              </Box>
            )}
            {meta && <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>{meta}</Box>}
          </Box>
          {actions && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>{actions}</Box>}
        </Box>
      </Box>
      {filters && <StickyFilterBar>{filters}</StickyFilterBar>}
    </>
  );
};

// Barra de filtros globales: fina, pegada bajo la cabecera y fija al desplazarse.
export const StickyFilterBar: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
  <Box sx={{ position: 'sticky', top: 8, zIndex: 5, mb: 2.5, py: 0.75, px: 1.25, borderRadius: '12px', bgcolor: 'rgba(8,17,32,.9)', backdropFilter: 'blur(10px)', border: `1px solid ${J.border}`, ...sx }}>
    {children}
  </Box>
);

export default PageHeader;

// PRC-035: marco de todas las pantallas de Pricing: ancho completo hasta 1.680 px y 32 px de margen.
export const pageFrameSx = {
  width: '100%',
  maxWidth: 1680,
  mx: 'auto',
  boxSizing: 'border-box',
  px: { xs: 2, md: 4 },
  pt: 3,
  pb: 6,
} as const;

// Altura de una barra fija (filtros) para que la cabecera de una tabla quede justo debajo.
export function useStickyOffset<T extends HTMLElement>(gap = 8): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setOffset(el.offsetHeight ? el.offsetHeight + gap + 8 : 0);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [gap]);
  return [ref, offset];
}
